<template>
  <ValidationObserver ref="observer">
    <template #default="{ handleSubmit, invalid }">
      <form class="text-sm" @submit.prevent="handleSubmit(doSubmit)">
        <ul class="flex border-b-2 select-none">
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isRecipientTab ? 'border-blue-600' : '',
              isRecipientTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusRecipientTab"
          >
            Recipient Details
          </li>
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isIssuerTab ? 'border-blue-600' : '',
              isIssuerTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusIssuerTab"
          >
            Issuer Details
          </li>
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isInvoiceTab ? 'border-blue-600' : '',
              isInvoiceTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusInvoiceTab"
          >
            Invoice Details
          </li>
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isPreviewTab ? 'border-blue-600' : '',
              isPreviewTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusPreviewTab"
          >
            Preview
          </li>
        </ul>

        <div class="flex -mx-4 mt-12">
          <div class="flex-1 px-4">
            <RecipientDetails
              v-show="isRecipientTab"
              key="recipient_details"
              :account-uuid="data.recipientUuid"
              :detail-options="recipientDetailOptions"
              :name.sync="data.recipientName"
              :address.sync="data.recipientAddress"
              :vat-number.sync="data.recipientVatNumber"
              :vat-office.sync="data.recipientVatOffice"
              :disabled="disabled"
            />

            <div v-show="isIssuerTab" key="issuer_details">
              <BaseFormGroup v-if="!data.uuid" class="mb-8">
                <BaseFormLabel> Account </BaseFormLabel>
                <VSelect
                  v-model="data.issuerUuid"
                  :options="issuerOptions"
                  :reduce="(issuer) => issuer.value"
                  class="vue-select w-64"
                />
              </BaseFormGroup>
              <IssuerDetails
                :account-uuid="data.issuerUuid"
                :detail-options="issuerDetailOptions"
                :name.sync="data.issuerName"
                :address.sync="data.issuerAddress"
                :vat-number.sync="data.issuerVatNumber"
                :vat-office.sync="data.issuerVatOffice"
                :disabled="disabled"
              />
            </div>

            <div v-show="isInvoiceTab" key="invoice_details">
              <InvoiceDetails
                :invoice-issue-date.sync="data.issueDate"
                :invoice-number.sync="data.number"
                :invoice-type.sync="data.type"
                :disabled="disabled"
                :locked="hasUuid"
                class="mb-12"
              />
              <InvoiceLines :invoice-lines.sync="dataLines" :disabled="disabled" />
            </div>

            <div
              v-show="isPreviewTab"
              key="invoice_preview"
              class="flex flex-col items-center space-y-4"
            >
              <BaseButton
                v-if="invoicePreview.uuid"
                tag="router-link"
                small
                primary
                :to="{
                  name: 'invoices.invoiceId.preview',
                  params: { invoiceId: invoicePreview.uuid },
                }"
                target="_blank"
              >
                Open in another tab
              </BaseButton>
              <div class="bg-white rounded-lg border border-gray-400 overflow-hidden pb-16">
                <PrintableInvoice
                  v-if="accountRelation"
                  :invoice="invoicePreview"
                  :account-relation="accountRelation"
                  class="mb-12"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="$refs.observer && $refs.observer.ctx && $refs.observer.ctx.invalid"
          class="bg-red-100 text-red-600 rounded p-4 mt-16 border border-red-600"
        >
          <ul class="list-disc list-inside">
            <template v-for="(provider, key) in $refs.observer.refs">
              <li
                v-for="(message, errorIdx) in getProviderMessages(provider)"
                :key="`${key}_${errorIdx}`"
              >
                {{ message }}
              </li>
            </template>
          </ul>
        </div>

        <div class="mt-16 border-t pt-12 text-right">
          <BaseButton primary class="px-6" :disabled="disabled || invalid"> Submit </BaseButton>
        </div>
      </form>
    </template>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import RecipientDetailsVue from './invoiceForm/RecipientDetails.vue';
import IssuerDetailsVue from './invoiceForm/IssuerDetails.vue';
import InvoiceLinesVue from './invoiceForm/InvoiceLines.vue';
import InvoiceDetailsVue from './invoiceForm/InvoiceDetails.vue';
import PrintableInvoice from './PrintableInvoice.vue';

const TAB_RECIPIENT = 'recipient';
const TAB_ISSUER = 'issuer';
const TAB_INVOICE = 'invoice';
const TAB_PREVIEW = 'preview';

export default {
  components: {
    ValidationObserver,
    IssuerDetails: IssuerDetailsVue,
    RecipientDetails: RecipientDetailsVue,
    InvoiceLines: InvoiceLinesVue,
    InvoiceDetails: InvoiceDetailsVue,
    PrintableInvoice,
  },

  props: {
    defaultData: {
      type: Object,
      required: true,
    },
    defaultLines: {
      type: Array,
      default: () => [],
    },
    recipientOptions: {
      type: Array,
      default: () => [],
    },
    issuerOptions: {
      type: Array,
      default: () => [],
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
    disabled: Boolean,
  },

  data() {
    return {
      currentTab: TAB_RECIPIENT,
      data: {
        issueDate: '',
        number: '',
        type: 'invoice',
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
        issuerUuid: null,
        issuerName: '',
        issuerAddress: '',
        issuerVatNumber: '',
        issuerVatOffice: '',
        ...this.defaultData,
      },
      dataLines: [...this.defaultLines],
    };
  },

  computed: {
    isRecipientTab() {
      return this.currentTab === TAB_RECIPIENT;
    },
    isIssuerTab() {
      return this.currentTab === TAB_ISSUER;
    },
    isInvoiceTab() {
      return this.currentTab === TAB_INVOICE;
    },
    isPreviewTab() {
      return this.currentTab === TAB_PREVIEW;
    },
    accountRelation() {
      if (typeof this.data.account_relation_uuid !== 'string') {
        return null;
      }

      return this.$store.state.entities.accountRelations.byId[this.data.account_relation_uuid];
    },
    invoicePreview() {
      return {
        ...this.data,
        invoiceLines: [...this.dataLines],
      };
    },
    recipient() {
      return (
        this.recipientOptions.find(
          (option) =>
            option.value === this.data.recipientUuid ||
            option.value === this.accountRelation?.related_account_uuid
        ) || null
      );
    },
    issuer() {
      return (
        this.issuerOptions.find(
          (option) =>
            option.value === this.data.issuerUuid || option.value === this.data.account_uuid
        ) || null
      );
    },
    recipientDetailOptions() {
      if (!this.recipient) return [];
      return this.recipient.registrations;
    },
    issuerDetailOptions() {
      if (!this.issuer) return [];
      return this.issuer.registrations;
    },
    hasUuid() {
      return typeof this.defaultData.uuid === 'string';
    },
  },

  watch: {
    defaultData: {
      deep: true,
      handler(newDefaultData) {
        this.data = { ...this.data, ...newDefaultData };
      },
    },
    defaultLines: {
      deep: true,
      handler(newDefaultLines) {
        this.dataLines = [...newDefaultLines];
      },
    },
    'data.issuerUuid': {
      handler() {
        this.setDefaultIssuerDetails();
      },
    },
    serverErrors: {
      deep: true,
      handler: 'applyServerErrors',
    },
  },

  methods: {
    focusRecipientTab() {
      this.currentTab = TAB_RECIPIENT;
    },
    focusIssuerTab() {
      this.currentTab = TAB_ISSUER;
    },
    focusInvoiceTab() {
      this.currentTab = TAB_INVOICE;
    },
    focusPreviewTab() {
      this.currentTab = TAB_PREVIEW;
    },
    setDefaultIssuerDetails() {
      if (this.issuerDetailOptions.length === 0) return;

      const defaultDetails = this.issuerDetailOptions.slice().pop();

      this.data.issuerName = defaultDetails.businessName;
      this.data.issuerAddress = defaultDetails.businessAddress;
      this.data.issuerVatNumber = defaultDetails.number;
      this.data.issuerVatOffice = defaultDetails.office;
    },
    doSubmit() {
      this.$emit('invoice-form:submit', {
        ...this.data,
        invoiceLines: [...this.dataLines],
      });
    },
    getProviderMessages(provider) {
      if (!provider.messages) return [];

      return provider.messages;
    },
    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
